import { ArrowDownWideNarrow, Globe, Lock, Network, Unlock } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, CardSubtitle, CardTitle, Skeleton } from '@/components/ui';
import { useNotify } from '@/hooks/useNotify';
import { useT } from '@/i18n';
import { api, type IpamAddress, type IpamIpv4Pool, type IpamIpv6Pool } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatNumber } from '@/lib/format';
import { paths } from '@/router/paths';

/**
 * Visuelle IP-Verwaltung.
 *
 * Der IPv4-Pool wird als Raster dargestellt und absteigend sortiert - in genau
 * der Reihenfolge, in der die Adressen auch vergeben werden. So ist auf einen
 * Blick erkennbar, welche Adresse als nächste an eine Instanz geht.
 */
export function AdminIpamPage() {
  const t = useT();
  const notify = useNotify();
  const [ipv4Pools, setIpv4Pools] = useState<IpamIpv4Pool[]>([]);
  const [ipv6Pools, setIpv6Pools] = useState<IpamIpv6Pool[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<IpamAddress | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.admin.ipam();
      setIpv4Pools(data.ipv4Pools);
      setIpv6Pools(data.ipv6Pools);
    } catch (cause) {
      notify.fromError(cause, t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
    // absichtlich nur beim ersten Rendern
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleReservation = async (address: IpamAddress) => {
    const next = address.status === 'RESERVED' ? 'AVAILABLE' : 'RESERVED';
    try {
      await api.admin.setAddressStatus(address.id, next);
      notify.success(next === 'RESERVED' ? t('admin.ipamReserved_toast') : t('admin.ipamReleased'));
      setSelected(null);
      await load();
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    }
  };

  if (loading) return <Skeleton className="h-[420px]" />;

  return (
    <div className="space-y-4">
      {ipv4Pools.length === 0 && ipv6Pools.length === 0 && (
        <Card>
          <p className="py-8 text-center text-[13px] text-t2">{t('admin.ipamNoPools')}</p>
        </Card>
      )}

      {ipv4Pools.map((pool) => {
        const next = pool.addresses.find((address) => address.status === 'AVAILABLE');
        return (
          <Card key={pool.id} padding="none">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-[11px] bg-acc-soft text-acc">
                  <Globe size={17} />
                </span>
                <div>
                  <CardTitle>{pool.cidr}</CardTitle>
                  <CardSubtitle>
                    {pool.server.name}
                    {pool.gateway ? ` · ${t('admin.ipamGateway')} ${pool.gateway}` : ''} ·{' '}
                    {pool.server.wanInterface}
                  </CardSubtitle>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="accent">
                  <ArrowDownWideNarrow size={12} />
                  {pool.strategy === 'DESCENDING'
                    ? t('admin.strategyDescending')
                    : t('admin.strategyAscending')}
                </Badge>
                {next && (
                  <Badge variant="info">
                    {t('admin.ipamNextAssignment')}: {next.address}
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-4 border-b border-line px-5 py-3 text-[12.5px]">
              <Legend color="var(--ok)" label={t('admin.ipamAvailable')} count={pool.available} />
              <Legend color="var(--info)" label={t('admin.ipamAssigned')} count={pool.assigned} />
              <Legend color="var(--warn)" label={t('admin.ipamReserved')} count={pool.reserved} />
              <span className="ml-auto text-t2">
                {formatNumber(pool.total)} {t('common.total')}
              </span>
            </div>

            {/* Raster in Vergabereihenfolge: oben links die nächste Adresse */}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-1.5 p-5">
              {pool.addresses.map((address) => (
                <button
                  key={address.id}
                  type="button"
                  onClick={() => setSelected(address)}
                  title={
                    address.instance
                      ? t('admin.ipamAssignedTo', { instance: address.instance.name })
                      : address.address
                  }
                  className={cn(
                    'rounded-[var(--radius-vela-sm)] border px-2 py-1.5 text-left font-mono text-[11.5px] transition-colors',
                    address.status === 'AVAILABLE' &&
                      'border-ok/30 bg-ok-soft text-ok hover:border-ok/60',
                    address.status === 'ASSIGNED' &&
                      'border-info/30 bg-info-soft text-info hover:border-info/60',
                    address.status === 'RESERVED' &&
                      'border-warn/30 bg-warn-soft text-warn hover:border-warn/60',
                    selected?.id === address.id && 'ring-2 ring-acc',
                  )}
                >
                  <span className="block truncate">{address.address.split('.').slice(-1)[0]}</span>
                  <span className="block truncate text-[9.5px] opacity-70">
                    {address.instance ? address.instance.ifName : address.status.slice(0, 4)}
                  </span>
                </button>
              ))}
            </div>

            {selected && pool.addresses.some((address) => address.id === selected.id) && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-bg-inset px-5 py-3">
                <div className="font-mono text-[13px] text-t0">
                  {selected.address}
                  {selected.instance && (
                    <Link
                      to={paths.instances.detail(selected.instance.id)}
                      className="ml-2 font-sans text-[12px] text-acc hover:underline"
                    >
                      {selected.instance.name} ({selected.instance.ifName})
                    </Link>
                  )}
                </div>

                {!selected.instance && (
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={selected.status === 'RESERVED' ? <Unlock size={14} /> : <Lock size={14} />}
                    onClick={() => void toggleReservation(selected)}
                  >
                    {selected.status === 'RESERVED' ? t('admin.ipamRelease') : t('admin.ipamReserve')}
                  </Button>
                )}
              </div>
            )}
          </Card>
        );
      })}

      {ipv6Pools.map((pool) => (
        <Card key={pool.id} padding="none">
          <div className="flex items-start gap-3 border-b border-line px-5 py-4">
            <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-[11px] bg-info-soft text-info">
              <Network size={17} />
            </span>
            <div>
              <CardTitle>{pool.cidr}</CardTitle>
              <CardSubtitle>
                {pool.server.name}
                {pool.gateway ? ` · ${t('admin.ipamGateway')} ${pool.gateway}` : ''}
              </CardSubtitle>
            </div>
          </div>

          <div className="p-5">
            <p className="mb-2 text-[10.5px] font-bold tracking-wider text-t2 uppercase">
              {t('admin.ipv6Allocations')} ({pool.allocations.length})
            </p>

            {pool.allocations.length === 0 ? (
              <p className="text-[13px] text-t2">{t('admin.ipv6NoAllocations')}</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {pool.allocations.map((allocation) => (
                  <Badge
                    key={allocation.id}
                    variant={allocation.instance ? 'info' : 'neutral'}
                    className="font-mono"
                  >
                    {allocation.cidr}
                    {allocation.instance ? ` · ${allocation.instance.ifName}` : ''}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

function Legend({ color, label, count }: { color: string; label: string; count: number }) {
  return (
    <span className="flex items-center gap-1.5 text-t1">
      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
      {label}
      <span className="font-bold text-t0 tabular-nums">{formatNumber(count)}</span>
    </span>
  );
}
